import type { EditorPropType } from "replo-runtime/shared/types";
import type { Component } from "schemas/component";

import * as React from "react";

import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import usePaintSharedState, {
  usePaintSharedStateValue,
} from "@v1/hooks/usePaintSharedState";
import { useSyncRuntimeValue } from "@v1/hooks/useSyncRuntimeValue";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Eye,
  EyeClosed,
  Zap,
} from "lucide-react";

type EditorDataRenderProps = {
  value: any;
  id: string;
  component: Component;

  onChange: Function;
  description: string;
  componentId: string;
};

type EditorData = {
  render(props: EditorDataRenderProps): React.ReactNode;
  defaultValue: any;
};

export const getEditorPropEditorData = (
  editorPropType: EditorPropType,
): EditorData => {
  return editorPropTypeToEditorData[editorPropType]!;
};

export const canRenderEditorControl = (
  editorPropType: EditorPropType,
  component: Component,
) => {
  if (editorPropType === "interactions") {
    return (component.props.onClick ?? []).length === 0;
  }

  return true;
};

const editorPropTypeToEditorData: Record<EditorPropType, EditorData | null> = {
  index: {
    render: (props) => {
      return <ActiveIndexControl {...props} />;
    },
    defaultValue: 0,
  },
  visibility: {
    render: (props) => {
      return <VisibilityControl {...props} />;
    },
    defaultValue: false,
  },
  collapsibility: {
    render: (props) => {
      return <CollapsibilityControl {...props} />;
    },
    defaultValue: false,
  },
  interactions: {
    render: (props) => {
      return <InteractionsControl {...props} />;
    },
    defaultValue: false,
  },
  activeBeforeAfterState: {
    render: (props) => {
      return <ActiveBeforeAfterSlideControl {...props} />;
    },
    defaultValue: "both",
  },
};

const ActiveIndexControl: React.FC<EditorDataRenderProps> = ({
  value,
  onChange,
  componentId,
}) => {
  const totalItems = useSyncRuntimeValue(`${componentId}.totalItems`);
  if (!totalItems || typeof totalItems !== "number") {
    return null;
  }

  return (
    <div className="flex items-center">
      <DeprecatedIconButton
        icon={<ChevronLeft size={16} />}
        variant="primary"
        onClick={() => {
          if (value > 0) {
            onChange(value - 1);
          }
        }}
        style={{
          backgroundColor: "transparent",
          padding: 0,
        }}
        tooltipText="Previous"
      />
      <div className="whitespace-nowrap border-x border-white/25 px-3">
        {value + 1} of {totalItems}
      </div>
      <DeprecatedIconButton
        icon={<ChevronRight size={16} />}
        variant="primary"
        onClick={() => {
          if (value < totalItems - 1) {
            onChange(value + 1);
          }
        }}
        style={{
          backgroundColor: "transparent",
          padding: 0,
        }}
        tooltipText="Next"
      />
    </div>
  );
};

const VisibilityControl: React.FC<EditorDataRenderProps> = ({
  value: isVisible,
  onChange,
  component,
}) => {
  const label = `${isVisible ? "Hide" : "Show"} ${component.name}`;
  return (
    <DeprecatedIconButton
      icon={isVisible ? <Eye size={16} /> : <EyeClosed size={16} />}
      variant="primary"
      onClick={() => {
        onChange(!isVisible);
      }}
      style={{
        backgroundColor: "transparent",
        padding: 0,
      }}
      aria-label={label}
      tooltipText={label}
    />
  );
};

const CollapsibilityControl: React.FC<EditorDataRenderProps> = ({
  value: isOpen,
  onChange,
}) => {
  return (
    <DeprecatedIconButton
      icon={
        isOpen ? <ChevronsDownUp size={18} /> : <ChevronsUpDown size={18} />
      }
      variant="primary"
      onClick={() => {
        onChange(!isOpen);
      }}
      style={{
        backgroundColor: "transparent",
        padding: 0,
      }}
      tooltipText={isOpen ? "Collapse" : "Expand"}
    />
  );
};

const InteractionsControl: React.FC<EditorDataRenderProps> = ({
  onChange,
  description,
}) => {
  return (
    <button
      onClick={() => onChange()}
      title={description}
      className="cursor-pointer"
    >
      <Zap size={16} className="text-white/50 hover:text-white" />
    </button>
  );
};

type BeforeAfterControlState = "before" | "both" | "after" | null;

const ActiveBeforeAfterSlideControl: React.FC<EditorDataRenderProps> = ({
  value,
  onChange,
  componentId,
}) => {
  const { setSharedState } = usePaintSharedState();
  const [position] = usePaintSharedStateValue(`${componentId}.position`);

  const onPositionChange = (value: BeforeAfterControlState) => {
    let newValue: BeforeAfterControlState = null;
    let newPosition: number | null = null;

    if (value === "before") {
      newValue = "before";
      newPosition = 100;
    } else if (value === "both") {
      newValue = "both";
      newPosition = 50;
    } else {
      newValue = "after";
      newPosition = 0;
    }

    if (newValue && newPosition !== null) {
      onChange(newValue);
      setSharedState({
        key: `${componentId}.position`,
        value: newPosition,
      });
    }
  };

  // NOTE (Sebas, 2024-06-04): This effect is used to update the position of the slider
  // when the user changes the selected component in the editor.
  React.useEffect(() => {
    if (position === 0) {
      onChange("after");
    }
    if (position === 50) {
      onChange("both");
    }
    if (position === 100) {
      onChange("before");
    }
  }, [onChange, position]);

  const selectableOptions = [
    { value: "before", label: "Before" },
    { value: "both", label: "Both" },
    { value: "after", label: "After" },
  ];

  return (
    <Combobox.Root
      options={selectableOptions}
      value={value}
      onChange={(value) => onPositionChange(value as BeforeAfterControlState)}
    >
      <Combobox.Trigger>
        <div className="flex cursor-pointer items-center justify-between bg-transparent text-sm text-white">
          <span className="pr-1">
            {value
              ? selectableOptions.find((opt) => opt.value === value)?.label
              : "Position"}
          </span>
          <ChevronDown className="text-white" size={8} />
        </div>
      </Combobox.Trigger>
      <Combobox.Popover align="center" sideOffset={8} layoutClassName="w-24">
        <Combobox.Content />
      </Combobox.Popover>
    </Combobox.Root>
  );
};
